/**
 * Terms of Service Page
 * 
 * Required for Apple App Store and Google Play Store submissions
 * Styled to match the main landing page
 */

'use client';

import Link from 'next/link';
import { Mail, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
    const lastUpdated = 'February 6, 2026';
    const companyName = 'TekSpert';
    const appName = 'TekSpert Mail';
    const contactEmail = 'legal@tekspert.com';
    const websiteUrl = 'https://mail.tekspert.com';

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/40 dark:from-gray-900 dark:via-blue-950/30 dark:to-indigo-950/40">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <Link href="/" className="flex items-center space-x-3 group">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center transform group-hover:scale-105 transition-transform">
                                <Mail className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">TekSpert Email</span>
                        </Link>

                        <div className="hidden md:flex items-center space-x-8">
                            <Link href="/#features" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                Features
                            </Link>
                            <Link href="/#security" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                Security
                            </Link>
                            <Link href="/#about" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                About
                            </Link>
                            <Link href="/#contact" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                Contact
                            </Link>
                        </div>

                        <div className="flex items-center space-x-4">
                            <Link
                                href="/auth/login"
                                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
                            >
                                Sign In
                            </Link>
                            <Link
                                href="/auth/register"
                                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transform hover:-translate-y-0.5 transition-all"
                            >
                                Get Started
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-32 pb-20 px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    {/* Back Link */}
                    <Link href="/" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-8 group">
                        <ArrowLeft className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" />
                        Back to Home
                    </Link>

                    {/* Content Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 sm:p-12">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                            Terms of Service
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 mb-10">
                            Last updated: {lastUpdated}
                        </p>

                        <div className="prose prose-lg prose-gray dark:prose-invert max-w-none">
                            {/* Acceptance of Terms */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    1. Acceptance of Terms
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    By accessing or using {appName} (the &quot;Service&quot;), you agree to be bound by these 
                                    Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, please do not 
                                    use the Service. {companyName} (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) reserves the right to 
                                    modify these Terms at any time.
                                </p>
                            </section>

                            {/* Description of Service */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    2. Description of Service
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {appName} is an email client application that allows you to send, receive, and 
                                    manage email messages. The Service is available on web browsers, iOS, and Android 
                                    devices. We may update, modify, or discontinue any aspect of the Service at any time.
                                </p>
                            </section>

                            {/* Account Registration */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    3. Account Registration
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    To use the Service, you must create an account. You agree to:
                                </p>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li>Provide accurate, current, and complete information during registration</li>
                                    <li>Maintain and promptly update your account information</li>
                                    <li>Keep your password secure and confidential</li>
                                    <li>Accept responsibility for all activities under your account</li>
                                    <li>Notify us immediately of any unauthorized use of your account</li>
                                </ul>
                            </section>

                            {/* Acceptable Use */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    4. Acceptable Use Policy
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    You agree not to use the Service to:
                                </p>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li>Send spam, unsolicited messages, or bulk commercial emails</li>
                                    <li>Transmit viruses, malware, or other harmful code</li>
                                    <li>Harass, threaten, or abuse other users</li>
                                    <li>Violate any applicable laws or regulations</li>
                                    <li>Infringe on intellectual property rights of others</li>
                                    <li>Attempt to gain unauthorized access to systems or networks</li>
                                    <li>Impersonate any person or entity</li>
                                    <li>Interfere with or disrupt the Service</li>
                                </ul>
                            </section>

                            {/* Intellectual Property */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    5. Intellectual Property Rights
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    The Service and its original content, features, and functionality are owned by 
                                    {companyName} and are protected by international copyright, trademark, patent, 
                                    trade secret, and other intellectual property laws. You may not copy, modify, 
                                    distribute, sell, or lease any part of our Service without our prior written consent.
                                </p>
                            </section>

                            {/* User Content */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    6. User Content
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    You retain ownership of all content you create, upload, or transmit through the Service. 
                                    By using the Service, you grant us a limited license to process and store your content 
                                    solely for the purpose of providing the Service to you. We do not claim any ownership 
                                    rights to your email content or attachments.
                                </p>
                            </section>

                            {/* Privacy */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    7. Privacy
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Your use of the Service is also governed by our <Link href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</Link>, 
                                    which is incorporated into these Terms by reference. Please review our Privacy Policy 
                                    to understand our practices regarding your personal information.
                                </p>
                            </section>

                            {/* Third-Party Services */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    8. Third-Party Services
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    The Service may integrate with or contain links to third-party websites, services, 
                                    or applications. We are not responsible for the content, privacy policies, or practices 
                                    of any third parties. Your use of third-party services is at your own risk.
                                </p>
                            </section>

                            {/* Disclaimers */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    9. Disclaimers
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, 
                                    EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
                                    FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE 
                                    WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
                                </p>
                            </section>

                            {/* Limitation of Liability */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    10. Limitation of Liability
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY 
                                    INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, 
                                    DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
                                </p>
                            </section>

                            {/* Indemnification */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    11. Indemnification
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    You agree to defend, indemnify, and hold harmless {companyName}, its officers, directors, 
                                    employees, and agents from any claims, damages, losses, liabilities, and expenses 
                                    (including attorneys&apos; fees) arising out of your use of the Service or violation of these Terms.
                                </p>
                            </section>

                            {/* Termination */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    12. Termination
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    We may terminate or suspend your account and access to the Service immediately, 
                                    without prior notice, for any reason, including breach of these Terms. Upon termination, 
                                    your right to use the Service will immediately cease. You may terminate your account at 
                                    any time by contacting us.
                                </p>
                            </section>

                            {/* Governing Law */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    13. Governing Law
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    These Terms shall be governed by and construed in accordance with the laws of 
                                    England and Wales, without regard to its conflict of law provisions. Any disputes 
                                    arising from these Terms shall be resolved in the courts of England and Wales.
                                </p>
                            </section>

                            {/* Changes to Terms */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    14. Changes to Terms
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    We reserve the right to modify these Terms at any time. We will notify you of any 
                                    material changes by posting the new Terms on this page and updating the &quot;Last updated&quot; 
                                    date. Your continued use of the Service after any changes constitutes acceptance 
                                    of the new Terms.
                                </p>
                            </section>

                            {/* Contact Us */}
                            <section className="mb-6">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    15. Contact Us
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    If you have any questions about these Terms, please contact us:
                                </p>
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-700 rounded-xl p-6">
                                    <p className="text-gray-700 dark:text-gray-300">
                                        <strong className="text-gray-900 dark:text-white">{companyName}</strong><br />
                                        Email: <a href={`mailto:${contactEmail}`} className="text-blue-600 dark:text-blue-400 hover:underline">{contactEmail}</a><br />
                                        Website: <a href={websiteUrl} className="text-blue-600 dark:text-blue-400 hover:underline">{websiteUrl}</a>
                                    </p>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 dark:bg-black text-gray-300 py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        {/* Company Info */}
                        <div>
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-xl font-bold text-white">TekSpert</span>
                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Enterprise email solutions for modern businesses.
                            </p>
                        </div>

                        {/* Product */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Product</h4>
                            <ul className="space-y-2">
                                <li><Link href="/#features" className="hover:text-blue-400 transition-colors">Features</Link></li>
                                <li><Link href="/#security" className="hover:text-blue-400 transition-colors">Security</Link></li>
                                <li><Link href="/auth/register" className="hover:text-blue-400 transition-colors">Pricing</Link></li>
                                <li><Link href="/#about" className="hover:text-blue-400 transition-colors">About</Link></li>
                            </ul>
                        </div>

                        {/* Support */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Support</h4>
                            <ul className="space-y-2">
                                <li><a href="mailto:support@tekspert.co.uk" className="hover:text-blue-400 transition-colors">Contact Support</a></li>
                                <li><Link href="/#contact" className="hover:text-blue-400 transition-colors">Help Center</Link></li>
                                <li><Link href="/#features" className="hover:text-blue-400 transition-colors">Getting Started</Link></li>
                            </ul>
                        </div>

                        {/* Company */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Company</h4>
                            <ul className="space-y-2">
                                <li><Link href="/#about" className="hover:text-blue-400 transition-colors">About Us</Link></li>
                                <li><a href="https://tekspert.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">TekSpert.co.uk</a></li>
                                <li><Link href="/privacy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link></li>
                                <li><Link href="/terms" className="text-blue-400">Terms of Service</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-gray-400 text-sm">
                            © {new Date().getFullYear()} TekSpert Email. All rights reserved.
                        </p>
                        <div className="flex items-center space-x-6">
                            <a href="mailto:support@tekspert.co.uk" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                                support@tekspert.co.uk
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
